import React, { useState, useEffect } from 'react';
import SearchInput from './SearchInput';
import SearchResults from './SearchResults';
import { makeStyles } from '@material-ui/core';
import { useSearchQuery } from 'src/hooks';
import { setSearchLocalStorage } from 'src/utils/search';
import { useHistory } from 'react-router-dom';
import { TrackGoogleAnalyticsEvent } from 'src/views/Marketplace/GoogleAnalyticsFunctionality';
/**
 * @typedef {import('src/lib/type').SearchHit} SearchHit
 */

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    position: 'relative',
  },
  searchInputWrapper: {
    maxWidth: '600px',
    transition: 'all 0.2s ease',
    '&.isOpen': {
      maxWidth: '100%',
    },
  },
  skeleton: {
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    borderRadius: '200px',
    maxWidth: '600px',
  },
  resultsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    boxShadow: theme.shadows[10],
    zIndex: 50,
  },
}));

/**
 * Main search component with column layout
 */
export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [isOpen, setOpen] = useState(false);
  const [isHybridSearchEnabled, setHybridSearchEnabled] = useState(false);
  const searchRef = React.useRef(null);
  const classes = useStyles();
  const [isSearchFocused, setSearchFocused] = useState(false);
  const history = useHistory();

  const { data: searchHits, isFetching, isError } = useSearchQuery(query, {
    isHybridSearchEnabled,
  });

  useEffect(() => {
    setOpen(isSearchFocused && query.length > 0);
  }, [query, setOpen, isSearchFocused]);

  // Handles input change
  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);
  };

  // Handles focus on the container (input or results)
  const handleContainerFocus = () => {
    setSearchFocused(true);
  };

  // Handles container blur (when focus completely leaves the search area)
  const handleContainerBlur = (event) => {
    // Check if the new focus is still within the search container
    if (!searchRef.current?.contains(event.relatedTarget)) {
      setSearchFocused(false);
    }
  };

  /**
   * Handles clicking on an element from the results list
   * @param {SearchHit} suggestion - The clicked suggestion
   * @returns {void}
   */
  const handleItemClick = (suggestion) => {
    if (suggestion.type === 'company') {
      const companySlug = suggestion.slug;
      if (!companySlug) {
        throw new Error('The company slug is not found');
      }
      TrackGoogleAnalyticsEvent(
        'company_clicked_marketplace',
        'company_clicked_marketplace',
        'Clicked on company card ' + companySlug
      );
      const url = `marketplace/company/${companySlug}`;

      return history.push(url);
    }

    TrackGoogleAnalyticsEvent(
      'use_hero_search',
      'use_hero_search',
      'Hero Search submit'
    );

    setSearchLocalStorage(suggestion);

    history.push(`/marketplace/homepage-search`);
  };

  return (
    <div
      className={`${classes.searchContainer}`}
      ref={searchRef}
      onFocus={handleContainerFocus}
      onBlur={handleContainerBlur}
      tabIndex={-1} // Allows the div to receive focus
    >
      <div className={`${classes.searchInputWrapper} ${isOpen && 'isOpen'}`}>
        <SearchInput
          query={query}
          onQueryChange={handleInputChange}
          isLoading={isFetching}
          onToggleChange={setHybridSearchEnabled}
          toggleEnabled={isHybridSearchEnabled}
        />
      </div>

      {/* Results dropdown */}
      {isOpen && query.length > 0 && (
        <div className={classes.resultsContainer}>
          <SearchResults
            suggestions={searchHits}
            isError={isError}
            onItemClick={handleItemClick}
          />
        </div>
      )}
    </div>
  );
}
